/*
 * Copyright Deskshare, Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Currency Context Provider
 *
 * Provides global currency state management for the application.
 * Features:
 * - Dynamic exchange rate fetching from backend API
 * - Location-based currency detection
 * - Client-side rate caching with staleness detection
 * - Currency conversion utilities
 * - Price formatting helpers
 * - Automatic background rate refresh
 *
 * @module contexts/CurrencyContext
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  fetchExchangeRates,
  convertLocally,
  getRate as getLocalRate,
  clearRateCache,
  type RateSource,
} from "@/lib/api/rates";
import { api } from "@/lib/http-client";
import { logger } from "@/lib/logger";
import { getCurrencySymbol } from "@/lib/currencies";

// ============================================================================
// Types & Interfaces
// ============================================================================

/**
 * Location detection result from backend
 */
interface LocationResult {
  country_code: string;
  country_name: string;
  currency_code: string;
  detected_via: "geoip2" | "external_api" | "default";
}

/**
 * Currency context state
 */
interface CurrencyContextState {
  // Rate data
  /** Current exchange rates relative to USD */
  rates: Record<string, number>;
  /** Source of rate data */
  rateSource: RateSource | null;
  /** When rates were last updated (ISO string) */
  lastUpdated: string | null;
  /** Whether rates are older than threshold (24h) */
  isStale: boolean;
  /** Age of rates in hours */
  ageHours: number;

  // User location & currency
  /** User's detected currency code (e.g., "EUR") */
  userCurrency: string;
  /** User's detected country code (e.g., "DE") */
  userCountry: string;
  /** User's country name */
  userCountryName: string;
  /** Currency symbol for user's currency */
  userCurrencySymbol: string;
  /** Whether user's currency differs from USD */
  isNonUSD: boolean;

  // Loading & error states
  /** Whether rates are being fetched */
  isLoading: boolean;
  /** Whether initial load has completed */
  isInitialized: boolean;
  /** Error message if fetch failed */
  error: string | null;

  // Actions
  /** Convert amount from USD to target currency */
  convertFromUSD: (amount: number, targetCurrency?: string) => number;
  /** Convert amount between any two currencies */
  convert: (amount: number, from: string, to: string) => number | null;
  /** Format price with currency symbol */
  formatPrice: (
    amount: number,
    currency?: string,
    options?: Intl.NumberFormatOptions,
  ) => string;
  /** Get exchange rate between two currencies */
  getRate: (from: string, to: string) => number | null;
  /** Force refresh rates from API */
  refreshRates: () => Promise<void>;
  /** Get currency symbol for a code */
  getCurrencySymbol: (currencyCode: string) => string;
  /** Check if currency is supported */
  isCurrencySupported: (currencyCode: string) => boolean;
}

// ============================================================================
// Context
// ============================================================================

const CurrencyContext = createContext<CurrencyContextState | undefined>(
  undefined,
);

// ============================================================================
// Provider Component
// ============================================================================

/**
 * Provider props
 */
interface CurrencyProviderProps {
  children: React.ReactNode;
  /** Initial currency code (optional, will detect if not provided) */
  initialCurrency?: string;
  /** Skip location detection (for testing) */
  skipLocationDetection?: boolean;
  /** Rate refresh interval in ms (default: 1 hour) */
  refreshInterval?: number;
}

/**
 * Currency Provider Component
 *
 * Wraps the application to provide global currency state and utilities.
 *
 * @example
 * ```tsx
 * // In main.tsx or App.tsx
 * <CurrencyProvider>
 *   <App />
 * </CurrencyProvider>
 *
 * // In component
 * const { convertFromUSD, formatPrice } = useCurrency();
 * const localPrice = formatPrice(convertFromUSD(99.99));
 * ```
 */
export const CurrencyProvider: React.FC<CurrencyProviderProps> = ({
  children,
  initialCurrency = "USD",
  skipLocationDetection = false,
  refreshInterval = 60 * 60 * 1000, // 1 hour
}) => {
  // State
  const [rates, setRates] = useState<Record<string, number>>({});
  const [rateSource, setRateSource] = useState<RateSource | null>(null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [isStale, setIsStale] = useState(false);
  const [ageHours, setAgeHours] = useState(0);

  const [userCurrency, setUserCurrency] = useState(initialCurrency);
  const [userCountry, setUserCountry] = useState("US");
  const [userCountryName, setUserCountryName] = useState("United States");

  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refs for cleanup
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);

  /**
   * Fetch exchange rates from backend
   */
  const fetchRates = useCallback(
    async (forceRefresh = false) => {
      try {
        const data = await fetchExchangeRates({
          forceRefresh,
          skipLocalCache: forceRefresh,
        });

        if (isMountedRef.current) {
          setRates(data.rates);
          setRateSource(data.source);
          setLastUpdated(data.last_updated);
          setIsStale(data.is_stale);
          setAgeHours(data.age_hours);
          setError(null);
        }
      } catch (err) {
        logger.error("Failed to fetch exchange rates:", err);
        if (isMountedRef.current) {
          // Keep existing rates on error, just log it
          if (Object.keys(rates).length === 0) {
            setError("Failed to load exchange rates");
          }
        }
      }
    },
    [rates],
  );

  /**
   * Detect user location and currency
   */
  const detectLocation = useCallback(async () => {
    if (skipLocationDetection) return;

    try {
      const response = await api.get<LocationResult>("/users/location");
      const data = response.data;

      if (isMountedRef.current) {
        setUserCurrency(data.currency_code);
        setUserCountry(data.country_code);
        setUserCountryName(data.country_name);
      }
    } catch (err) {
      logger.warn("Location detection failed, using defaults:", err);
      // Keep defaults (USD, US)
    }
  }, [skipLocationDetection]);

  /**
   * Initialize: fetch rates and detect location
   */
  useEffect(() => {
    let isCancelled = false;

    const initialize = async () => {
      setIsLoading(true);

      await Promise.all([fetchRates(), detectLocation()]);

      if (!isCancelled && isMountedRef.current) {
        setIsLoading(false);
        setIsInitialized(true);
      }
    };

    initialize();

    return () => {
      isCancelled = true;
    };
  }, [fetchRates, detectLocation]);

  /**
   * Set up background rate refresh
   */
  useEffect(() => {
    if (refreshInterval > 0) {
      refreshTimerRef.current = setInterval(() => {
        fetchRates(false);
      }, refreshInterval);
    }

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [refreshInterval, fetchRates]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // ============================================================================
  // Conversion & Formatting Functions
  // ============================================================================

  /**
   * Convert amount from USD to target currency
   */
  const convertFromUSD = useCallback(
    (amount: number, targetCurrency?: string): number => {
      const target = targetCurrency || userCurrency;
      const result = convertLocally(amount, "USD", target, rates);
      return result ?? amount; // Fallback to original amount if conversion fails
    },
    [rates, userCurrency],
  );

  /**
   * Convert amount between any two currencies
   */
  const convert = useCallback(
    (amount: number, from: string, to: string): number | null => {
      return convertLocally(amount, from, to, rates);
    },
    [rates],
  );

  /**
   * Get exchange rate between two currencies
   */
  const getRate = useCallback(
    (from: string, to: string): number | null => {
      return getLocalRate(from, to, rates);
    },
    [rates],
  );

  /**
   * Format price with currency symbol and localization
   */
  const formatPrice = useCallback(
    (
      amount: number,
      currency?: string,
      options?: Intl.NumberFormatOptions,
    ): string => {
      const currencyCode = currency || userCurrency;

      try {
        const formatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: currencyCode,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          ...options,
        });
        return formatter.format(amount);
      } catch {
        // Fallback for unsupported currencies
        const symbol = getCurrencySymbol(currencyCode);
        return `${symbol}${amount.toFixed(2)}`;
      }
    },
    [userCurrency],
  );

  /**
   * Force refresh rates from API
   */
  const refreshRates = useCallback(async () => {
    setIsLoading(true);
    clearRateCache();
    await fetchRates(true);
    setIsLoading(false);
  }, [fetchRates]);

  /**
   * Check if currency is supported
   */
  const isCurrencySupported = useCallback(
    (currencyCode: string): boolean => {
      return currencyCode.toUpperCase() in rates;
    },
    [rates],
  );

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue = useMemo<CurrencyContextState>(
    () => ({
      // Rate data
      rates,
      rateSource,
      lastUpdated,
      isStale,
      ageHours,

      // User location & currency
      userCurrency,
      userCountry,
      userCountryName,
      userCurrencySymbol: getCurrencySymbol(userCurrency),
      isNonUSD: userCurrency !== "USD",

      // Loading & error states
      isLoading,
      isInitialized,
      error,

      // Actions
      convertFromUSD,
      convert,
      formatPrice,
      getRate,
      refreshRates,
      getCurrencySymbol,
      isCurrencySupported,
    }),
    [
      rates,
      rateSource,
      lastUpdated,
      isStale,
      ageHours,
      userCurrency,
      userCountry,
      userCountryName,
      isLoading,
      isInitialized,
      error,
      convertFromUSD,
      convert,
      formatPrice,
      getRate,
      refreshRates,
      isCurrencySupported,
    ],
  );

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
};

// ============================================================================
// Hook
// ============================================================================

/**
 * Hook to access currency context
 *
 * @throws Error if used outside CurrencyProvider
 *
 * @example
 * ```tsx
 * function PriceDisplay({ usdPrice }: { usdPrice: number }) {
 *   const { convertFromUSD, formatPrice, isNonUSD } = useCurrencyContext();
 *
 *   const localPrice = convertFromUSD(usdPrice);
 *
 *   return (
 *     <div>
 *       <span>{formatPrice(usdPrice, 'USD')}</span>
 *       {isNonUSD && (
 *         <span className="text-gray-500">
 *           (~{formatPrice(localPrice)})
 *         </span>
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useCurrencyContext(): CurrencyContextState {
  const context = useContext(CurrencyContext);

  if (context === undefined) {
    throw new Error(
      "useCurrencyContext must be used within a CurrencyProvider",
    );
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { CurrencyContext };
export default CurrencyProvider;
